import TaskTimingSection from "@/components/tasks/timing/TaskTimingSection/TaskTimingSection";
import type { TaskTimingChangeReason, TaskTimingColors, TaskTimingValue } from "@/components/tasks/timing/TaskTimingFields/TaskTimingFields";
import type { TaskScheduleFormValue } from "@/components/tasks/schedule/TaskScheduleForm/TaskScheduleForm";
import { setTaskTimingStart, type TaskStatusValue } from "@/lib/taskTiming";
import { todayKey } from "@/lib/time";
import { emptyTaskTiming, setTaskStatusNow, transitionTaskStatus } from "@/lib/taskTimingController";
import {
  deriveTaskLifecycle,
  getTaskLifecycleLabel,
} from "@shared/taskLifecycle";

export type TaskStatusFormValue = TaskStatusValue;

export type TaskStatusChangeReason = "status" | TaskTimingChangeReason | "today" | "now" | "clear";

export type TaskStatusFormProps = {
  value: TaskStatusFormValue;
  schedule: TaskScheduleFormValue;
  colors: TaskTimingColors;
  onChange: (value: TaskStatusFormValue, reason: TaskStatusChangeReason) => void;
  onCommit?: (value: TaskStatusFormValue, reason: TaskStatusChangeReason) => void;
  now?: () => Date;
  onResultsClick?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export default function TaskStatusForm({
  value,
  schedule,
  colors,
  onChange,
  onCommit,
  now = () => new Date(),
  onResultsClick,
  open,
  onOpenChange,
}: TaskStatusFormProps) {
  const lifecycle = deriveTaskLifecycle(value);
  const headerTiming = lifecycle === "doing" || lifecycle === "done"
    ? value.timing
    : schedule.timing;

  function changeStatus(status: TaskStatusFormValue["status"]) {
    onChange(transitionTaskStatus(value, schedule, status, now()), "status");
  }

  function changeTiming(timing: TaskTimingValue, reason: TaskTimingChangeReason) {
    const startedAt = timing.startDate && timing.startTime
      ? combineDateTime(timing.startDate, timing.startTime)
      : null;
    const completedAt = timing.endDate && timing.endTime
      ? combineDateTime(timing.endDate, timing.endTime)
      : null;
    onChange({ ...value, startedAt, completedAt, timing }, reason);
  }

  function setToday() {
    const date = todayKey();
    const timing = value.timing.startTime && value.timing.durationMinutes
      ? setTaskTimingStart(value.timing, date, value.timing.startTime)
      : { ...value.timing, startDate: date, endDate: date };
    changeTiming(timing, "start-date");
  }

  function setNow() {
    onChange(setTaskStatusNow(value, now()), "now");
  }

  return (
    <TaskTimingSection
      kind="status"
      className="task-status-form"
      colors={colors}
      open={Boolean(open)}
      expandable={Boolean(onOpenChange)}
      onOpenChange={next => { if (onOpenChange) onOpenChange(next); else onResultsClick?.(); }}
      label={getTaskLifecycleLabel(lifecycle)}
      lifecycle={lifecycle}
      status={value.status}
      startedAt={value.startedAt}
      completedAt={value.completedAt}
      onStatusChange={changeStatus}
      headerTiming={headerTiming}
      timing={{
        value: value.timing,
        onChange: changeTiming,
        onDurationBlur: () => onCommit?.(value, "duration"),
      }}
      onToday={setToday}
      onNow={setNow}
      onClear={() => onChange({ status: "pending", startedAt: null, completedAt: null, timing: emptyTaskTiming() }, "clear")}
    />
  );
}

function combineDateTime(date: string, time: string) {
  const [year, month, day] = date.split("-").map(Number);
  const [hours, minutes] = time.split(":").map(Number);
  return new Date(year, month - 1, day, hours, minutes);
}
